import { z } from 'zod';
/**
 * Advanced Notification Center contracts.
 *
 * A notification carries two orthogonal axes:
 *  - `category` — WHAT the notification is about (the event-type bucket), used
 *    by the Mentions / Ticket Updates / PR-Development / System tabs.
 *  - `reason`   — WHY this recipient received it (their relationship to the
 *    entity at delivery time), used by the Assigned-to-Me / Watching tabs.
 *    Captured at creation so the inbox stays historically accurate even after
 *    assignment/watch state later changes.
 *
 * Modeled as Zod enums (validated at the app layer) rather than DB-native enums
 * so the Prisma schema stays portable between SQLite (dev) and Postgres (prod),
 * matching the rest of @atlas/contracts.
 */
/** Coarse bucket powering the notification tabs/filters (spec §15.4). */
export const NotificationCategory = z.enum([
    'MENTION',
    'ASSIGNED',
    'TICKET_UPDATE',
    'PR_DEVELOPMENT',
    'SYSTEM',
]);
/** The query-side category filter accepted by the API (adds the virtual buckets). */
export const NotificationCategoryFilter = z.enum([
    'ALL',
    'MENTION',
    'ASSIGNED', // -> reason = assigned
    'WATCHING', // -> reason = watching
    'TICKET_UPDATE',
    'PR_DEVELOPMENT',
    'SYSTEM',
]);
/** Why a given recipient received the notification. */
export const NotificationReason = z.enum([
    'mention',
    'assigned',
    'watching',
    'reporter',
    'system',
]);
/** Visual/urgency level (spec §7). Drives styling + toast eligibility. */
export const NotificationPriority = z.enum(['low', 'medium', 'high', 'critical']);
/**
 * Specific event type (spec §15.3). Superset of the legacy types
 * (assigned | mention | customer_reply | comment | status_change |
 * agent_blocked | release_gate | watch) so existing rows keep working.
 */
export const NotificationType = z.enum([
    // mentions
    'mention',
    // assignment / ticket lifecycle
    'assigned',
    'unassigned',
    'status_change',
    'priority_change',
    'due_date_change',
    'comment',
    'internal_note',
    'reaction',
    'attachment_added',
    'reopened',
    'closed',
    'customer_reply',
    'watch',
    // SLA
    'sla_warning',
    'sla_breached',
    // PR / development
    'pr_opened',
    'pr_updated',
    'pr_merged',
    'pr_closed',
    'build_passed',
    'build_failed',
    'deployment_started',
    'deployment_completed',
    'deployment_failed',
    'agent_started',
    'agent_created_pr',
    'agent_completed',
    'agent_blocked',
    'release_gate',
    'uat_review_open',
    'uat_review_reminder',
    // system
    'integration_sync_failed',
    'system_alert',
    'morning_brief_ready',
    'filter_share_requested',
]);
/** Maps each event type to its category + default priority. */
const META = {
    mention: { category: 'MENTION', priority: 'high' },
    assigned: { category: 'ASSIGNED', priority: 'high' },
    unassigned: { category: 'ASSIGNED', priority: 'medium' },
    status_change: { category: 'TICKET_UPDATE', priority: 'medium' },
    priority_change: { category: 'TICKET_UPDATE', priority: 'medium' },
    due_date_change: { category: 'TICKET_UPDATE', priority: 'medium' },
    comment: { category: 'TICKET_UPDATE', priority: 'medium' },
    internal_note: { category: 'TICKET_UPDATE', priority: 'medium' },
    reaction: { category: 'TICKET_UPDATE', priority: 'low' },
    attachment_added: { category: 'TICKET_UPDATE', priority: 'low' },
    reopened: { category: 'TICKET_UPDATE', priority: 'medium' },
    closed: { category: 'TICKET_UPDATE', priority: 'medium' },
    customer_reply: { category: 'TICKET_UPDATE', priority: 'high' },
    watch: { category: 'TICKET_UPDATE', priority: 'low' },
    sla_warning: { category: 'TICKET_UPDATE', priority: 'high' },
    sla_breached: { category: 'TICKET_UPDATE', priority: 'critical' },
    pr_opened: { category: 'PR_DEVELOPMENT', priority: 'medium' },
    pr_updated: { category: 'PR_DEVELOPMENT', priority: 'low' },
    pr_merged: { category: 'PR_DEVELOPMENT', priority: 'medium' },
    pr_closed: { category: 'PR_DEVELOPMENT', priority: 'medium' },
    build_passed: { category: 'PR_DEVELOPMENT', priority: 'low' },
    build_failed: { category: 'PR_DEVELOPMENT', priority: 'high' },
    deployment_started: { category: 'PR_DEVELOPMENT', priority: 'low' },
    deployment_completed: { category: 'PR_DEVELOPMENT', priority: 'medium' },
    deployment_failed: { category: 'PR_DEVELOPMENT', priority: 'critical' },
    agent_started: { category: 'PR_DEVELOPMENT', priority: 'low' },
    agent_created_pr: { category: 'PR_DEVELOPMENT', priority: 'medium' },
    agent_completed: { category: 'PR_DEVELOPMENT', priority: 'medium' },
    agent_blocked: { category: 'PR_DEVELOPMENT', priority: 'high' },
    release_gate: { category: 'PR_DEVELOPMENT', priority: 'high' },
    uat_review_open: { category: 'PR_DEVELOPMENT', priority: 'high' },
    uat_review_reminder: { category: 'PR_DEVELOPMENT', priority: 'high' },
    integration_sync_failed: { category: 'SYSTEM', priority: 'critical' },
    system_alert: { category: 'SYSTEM', priority: 'high' },
    morning_brief_ready: { category: 'SYSTEM', priority: 'medium' },
    filter_share_requested: { category: 'SYSTEM', priority: 'medium' },
};
/**
 * Resolve the category + default priority for an event type. Falls back to a
 * generic ticket update for any unrecognized/legacy type so callers never throw.
 */
export function notificationMeta(type) {
    return META[type] ?? { category: 'TICKET_UPDATE', priority: 'medium' };
}
/** Priority ordering for dedup tie-breaks (higher wins). */
export const NOTIFICATION_PRIORITY_RANK = {
    low: 0,
    medium: 1,
    high: 2,
    critical: 3,
};
//# sourceMappingURL=notification.js.map